import styledFuncs from 'app/styles/styledFuncs';
import { Opt, PropsOf } from 'app/tools/general.types';
import _ from 'lodash';
import React, { useReducer, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import IconLib, { PdfSvg } from 'app/shared/components/icon/icon-lib';
import Icon from 'app/shared/components/icon/Icon';
import clsx from 'clsx';
// import { Document, Page, pdfjs } from 'react-pdf';
import AppImage from '../app-image/AppImage';
import Button from '../button/Button';

const ImageInputPoint = styled.input`
  display: none;
`;

const ImagePreview = styled(AppImage)`
  max-width: calc(100% - ${styledFuncs.pad(2)});
`;

const UploadLabelButton = styled.label<{ $fullWidth?: boolean }>`
  ${p =>
    !(p.$fullWidth ?? true) &&
    css`
      align-self: flex-start;
      min-width: 60%;
    `}
`;
const ImageRemoveButton = styled(Button)`
  position: absolute;
  padding: ${styledFuncs.pad()};
  min-width: 30px;
  min-height: 30px;
  top: -5px;
  right: 15px;
  border-radius: 50%;
  z-index: 1;
`;

type Props = {
  acceptType?: string;
  buttonText:
    | React.ReactNode
    | ((change: boolean) => React.ReactNode);
  changeButtonText?: React.ReactNode;
  initialFileUrl?: string;
  onFileSelected: (imageFile: File | null) => unknown;
  placeholderImage?: React.ReactNode;
  buttonProps?: PropsOf<typeof Button>;
  className?: string;
};

export default function ImageInput({
  acceptType,
  buttonText,
  changeButtonText,
  placeholderImage,
  onFileSelected,
  initialFileUrl,
  buttonProps,
  className,
}: Props): JSX.Element {
  const [fileSelected, setFileSelected] = useState(false);
  const [pdfFile, setPdfFile] = useState('');
  // const [numPages, setNumPages] = useState(1);
  // const [pageNumber, setPageNumber] = useState(1);
  const [imagePreviewUrl, setImagePreviewUrl] = useReducer(
    (oldUrl: Opt<string>, newUrl: string) => {
      if (oldUrl && oldUrl !== newUrl) {
        URL.revokeObjectURL(oldUrl);
      }
      return newUrl as Opt<string>;
    },
    initialFileUrl
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/restrict-template-expressions
  // pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  const handleFileSelected: Props['onFileSelected'] = file => {
    onFileSelected(file);
    setFileSelected(true);
  };

  const handleImageSelect = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (!file) return;

    handleFileSelected(file);
    if (file.type === 'application/pdf') {
      setPdfFile(file.name);
    } else {
      setImagePreviewUrl(URL.createObjectURL(file));
    }
  };

  const src = imagePreviewUrl || initialFileUrl || '';

  const onError = () => {
    // File-get failed, therefore remove selected file
    onFileSelected(null);
    setImagePreviewUrl('');
    setPdfFile('');
  };

  const buttonTextFunction = _.isFunction(buttonText)
    ? buttonText
    : (change: boolean) => (change ? changeButtonText : buttonText);

  const onRemovePhoto = () => {
    // Remove on click
    onFileSelected(null);
    setFileSelected(false);
    setImagePreviewUrl('');
    setPdfFile('');
  };
  // const onDocumentLoadSuccess = () => {
  //   setNumPages(numPages);
  // };
  return (
    <>
      <div className="relative">
        {!pdfFile && (
          <ImagePreview
            alt="uploaded receipt"
            fallbackImgNode={placeholderImage}
            {...{ className, src, onError }}
          />
        )}

        <ImageRemoveButton
          onClick={onRemovePhoto}
          variant="contained"
          color="secondary"
          className={clsx(
            'w-3 h-3 border border-white border-solid',
            !imagePreviewUrl && 'hidden'
          )}
        >
          <Icon icon={IconLib.CROSS} size={18} />
        </ImageRemoveButton>

        {pdfFile && (
          // <div>
          //   <Document
          //     file={pdfFile}
          //     onLoadSuccess={() => onDocumentLoadSuccess}
          //   >
          //     <Page pageNumber={pageNumber} />
          //   </Document>
          //   <p>
          //     Page {pageNumber} of {numPages}
          //   </p>
          // </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px',
            }}
          >
            <PdfSvg style={{ height: '70px' }} />
            <span>{pdfFile}</span>
          </div>
        )}

        <ImageRemoveButton
          onClick={onRemovePhoto}
          variant="contained"
          color="secondary"
          className={clsx(
            'w-3 h-3 border border-white border-solid',
            !pdfFile && 'hidden'
          )}
        >
          <Icon icon={IconLib.CROSS} size={18} />
        </ImageRemoveButton>

        <ImageInputPoint
          id="image-input"
          accept={acceptType || 'image/*'}
          type="file"
          onChange={handleImageSelect}
        />
        <UploadLabelButton
          $fullWidth={buttonProps?.fullWidth}
          htmlFor="image-input"
        >
          <Button {...buttonProps} asSpan>
            {buttonTextFunction(fileSelected)}
          </Button>
        </UploadLabelButton>
      </div>
    </>
  );
}
